import { HttpEventType } from '@angular/common/http';
import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { ResourceTypeCodes } from 'src/app/core/_enums/resource.type.codes';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { WorkUnityCode } from 'src/app/core/_enums/work.unity.code';
import { Energie } from 'src/app/core/_models/energie';
import { Family } from 'src/app/core/_models/family';
import { FormulaComposition } from 'src/app/core/_models/formulaComposition';
import { Product } from 'src/app/core/_models/product';
import { ProjectType, compare } from 'src/app/core/_models/projectType';
import { Transport } from 'src/app/core/_models/transport';
import { Unity } from 'src/app/core/_models/unity';
import { UserEntity } from 'src/app/core/_models/userEntity';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { FamilyService } from 'src/app/core/_services/family.service';
import { ProductFormulaService } from 'src/app/core/_services/product.formula.service';
import { ProjectTypeService } from 'src/app/core/_services/projectTypes.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { positiveIfCondition, requiredIfCondition, validateProjectTypeRequired } from 'src/app/core/_utils/validation';
import { StoreSelect, UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { DialogAddCompositionComponent } from '../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogEditCompositionComponent } from '../shared/dialog-edit-composition/dialog-edit-composition.component';
import { DialogInputComponent } from '../shared/dialog-input/dialog-input.component';
import { DialogLeaveComponent } from '../shared/dialog-leave/dialog-leave.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { DialogSelectEuShareComponent } from '../shared/dialog-select-eu-share/dialog-select-eu-share.component';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, DoCheck {
  currentItem!: Product;
  form!: FormGroup;
  families: Family[] = [];
  unitys!: Unity[];
  perimeterId!: string;
  hasDensity: boolean = false;
  WorkUnityCode = WorkUnityCode;
  isHygrometryRateVisible!: boolean;
  fileToUpload?: File;
  formSubmitted?: boolean;
  manageProducts!: boolean;
  manageProductsEcoPro!: boolean;
  favoriteId?: string;
  justificationFilePath: string;
  unSaved: boolean = false;
  bondingLayerProducts?: Product[];
  isComplexProduct: boolean = false;
  isPipeProduct: boolean = false;
  isJoinProduct: boolean = false;

  transports!: Transport[];
  energies!: Energie[];
  compositionTouched?: boolean;

  projectTypes!: ProjectType[];

  showDensity: boolean = true;
  acuView!: boolean;

  versionId: string = '';
  dbVersions: Version[] = [];

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;
  @ViewChild('fceuFileInput', { static: false })
  fceuFileInput!: ElementRef;
  @ViewChild('familySelect', { static: false })
  familySelect!: any;
  @ViewChild('resourceSection', { static: false })
  resourceSection!: ElementRef;

  isAddingNewFamily: boolean = false;
  newFamilyName: string = '';
  newFamilyCode: string = '';
  newFamilyId: string = GlobalConstants.EMPTY_GUID;
  fileToUploadOnSave?: File = undefined;
  ceuFileToUploadOnSave?: File = undefined;

  constructor(
    private productFormulaService: ProductFormulaService,
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private authService: AuthService,
    private utilsService: UtilsService,
    private transportService: TransportService,
    private energieService: EnergieService,
    private versionService: VersionService,
    private familyService: FamilyService,
    private projectTypeService: ProjectTypeService,
    private spinnerService: SpinnerOverlayService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';

    this.transportService.getAll().subscribe((data) => (this.transports = data));

    this.energieService.getAllForTransportation().subscribe((data) => (this.energies = data));
  }

  ngOnInit(): void {
    this.perimeterId = this.authService.roleSubject.value.perimeterId;
    this.form = this.formBuilder.group(
      {
        resourceId: [],
        code: ['', Validators.required],
        displayName: ['', Validators.required],
        owner: [{ value: '', disabled: true }],
        creator: [{ value: '', disabled: true }],
        density: [null, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity, control, 'product_field_density') ?? positiveIfCondition(!this.isComplexProduct && this.hasDensity, control)],
        hygrometryRate: [0, Validators.max(100)],
        familyId: ['', Validators.required],
        workUnityId: ['', Validators.required],
        description: [''],
        endProduct: [],
        coatedFormula: [],
        whiteFormula: [],
        pipeProduct: [],
        joinProduct: [],
        cableProduct: [],
        status: [],
        ceu: this.formBuilder.group({
          en: [0, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_energy')],
          co: [1, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_co2')],
          gn: [2, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_natural_aggregates')],
          ae: [3, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_asphalt_aggregates')],
          mr: [4, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_recycled_materials')],
          drc: [5, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct, control, 'product_field_reused_earthwork')]
        }),
        fCeu: this.formBuilder.group({
          en: [0, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_energy')],
          co: [1, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_co2')],
          gn: [2, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_natural_aggregates')],
          ae: [3, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_asphalt_aggregates')],
          mr: [4, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_recycled_materials')],
          drc: [5, (control: AbstractControl) => requiredIfCondition(!this.isComplexProduct && this.hasDensity && this.showDensity, control, 'product_field_reused_earthwork')]
        }),
        fileName: [],
        fileDescription: [],
        fCeuFileName: [],
        fCeuFileDescription: [],
        roadProject: [],
        earthworkProject: [],
        isComplexProduct: [],
        isPipeProduct: [],
        isJoinProduct: [],
        projectType: [[], validateProjectTypeRequired('validation_project_type_required')],
        versionId: ['']
      }
      // { validators: atLeastOne('roadProject', 'earthworkProject') }
    );

    this.activeRoute.params.subscribe((params) => {
      this.versionService.getAll().subscribe((versions) => {
        // Chargement de la liste de toutes les versions
        this.dbVersions = versions;
        const id = params['id'];
        const resourceId = id === 'new' || id === '0' ? undefined : id;

        this.loadProduct(resourceId);
      });
    });
    this.unSaved = false;
  }

  async loadLists() {
    this.families = !history.state?.families?.length ? await this.familyService.getRessourceByTypeCode(ResourceTypeCodes.Produit).toPromise() : history.state.families;
    this.unitys = !history.state?.unitys?.length ? await this.productFormulaService.getUnity().toPromise() : history.state.unitys;
    this.projectTypes = !history.state?.projectTypes?.length ? await this.projectTypeService.getAll().toPromise() : history.state.projectTypes;

    this.versionService.getAll().subscribe((versions) => {
      this.dbVersions = versions;
    });
  }

  async loadProduct(resourceId?: string) {
    await this.loadLists();
    const state = {
      ...history.state,
      families: this.families,
      unitys: this.unitys,
      bondingLayerProducts: this.bondingLayerProducts,
      projectTypes: this.projectTypes
    };

    if (resourceId)
      this.productFormulaService.get(resourceId).subscribe(
        (item) => {
          this.initForm(this.form, state, item);
        },
        (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          this.router.navigate([SiteMapUrl.MenuProductFormula]);
        }
      );
    else this.initForm(this.form, state, {});
  }

  async canDeactivate(): Promise<boolean> {
    if (this.unSaved) {
      const dialogRef = this.dialog.open(DialogLeaveComponent);

      const rep = await dialogRef.afterClosed().toPromise();
      if (rep) {
        return true;
      }

      return false;
    }
    return true;
  }

  ngDoCheck(): void {
    if (this.authService?.accessesValues) {
      this.manageProducts = this.authService.accessesValues.includes(FunctionalityCode.ManageProducts);
      this.manageProductsEcoPro = this.authService.accessesValues.includes(FunctionalityCode.ManageProductEcoPro);
      this.acuView = this.authService.accessesValues.includes(FunctionalityCode.ACUView);
    }
  }

  //Initialiser le formulaire
  initForm(form: FormGroup, state: any, currentItem: Product) {
    form.reset();

    // Préselection de la dernière version dans la liste
    this.form.patchValue({
      versionId: this.dbVersions[0].versionId
    });

    this.currentItem = currentItem;

    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
        this.isComplexProduct = this.currentItem.isComplexProduct ?? false;
        form.patchValue({ isComplexProduct: this.isComplexProduct });

        if (currentItem.pipeProduct != undefined) {
          if (this.currentItem.pipeProduct == 1) {
            this.isPipeProduct = true;
            this.isJoinProduct = false;
          } else if (this.currentItem.pipeProduct == 2) {
            this.isPipeProduct = false;
            this.isJoinProduct = true;
          } else {
            this.isPipeProduct = false;
            this.isJoinProduct = false;
          }

          form.patchValue({ isPipeProduct: this.isPipeProduct });
          form.patchValue({ isJoinProduct: this.isJoinProduct });
        }

        this.unSaved = false;
      });
    } else {
      this.isComplexProduct = state.isComplexProduct;
      form.patchValue({ isComplexProduct: this.isComplexProduct });
    }

    this.familyChange(currentItem?.familyId);
    this.changeWorkUnity(this.unitys, currentItem?.workUnityId);

    this.projectTypes = state.projectTypes;

    form.patchValue(currentItem);

    this.unSaved = false;

    // Subscribe to form changes, ignoring changes only to versionId and ceu
    this.form.valueChanges
      .pipe(
        startWith(this.form.getRawValue()), // needed for distinctUntilChanged to have an initial value
        distinctUntilChanged((prev, curr) => {
          if (this.isFormDisable()) {
            // Remove versionId and ceu from comparison
            const { versionId: _, ceu: __, ...prevRest } = prev;
            const { versionId: ___, ceu: ____, ...currRest } = curr;

            return JSON.stringify(prevRest) === JSON.stringify(currRest);
          }

          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe(() => {
        this.unSaved = true;
      });
  }

  isFormDisable() {
    return !this.manageProducts || !(this.currentItem?.canUpdate || !this.currentItem?.resourceId);
  }

  onVersionChange(value: string) {
    this.productFormulaService.get(history.state.resourceId, value).subscribe((itm) => {
      this.setFormCeu(itm);
    });
  }

  setFormCeu(itm: any) {
    let isEmpty: boolean = Object.keys(itm.ceu).length == 0;
    if (isEmpty) {
      this.snackbarService.open(SnackbarTypes.ResourceNotAvailableForversion);
      /*this.snackbarService.showMessage(
        "La ressource n'existe pas dans cette version."
      );*/
    }

    this.form.patchValue({
      ceu: {
        en: isEmpty ? '' : itm.ceu.en,
        co: isEmpty ? '' : itm.ceu.co,
        gn: isEmpty ? '' : itm.ceu.gn,
        ae: isEmpty ? '' : itm.ceu.ae,
        mr: isEmpty ? '' : itm.ceu.mr,
        drc: isEmpty ? '' : itm.ceu.drc
      }
    });

    if (this.hasDensity) {
      let hasFCeu: boolean = itm.fCeu && itm.fCeu.en && itm.fCeu.co && itm.fCeu.gn && itm.fCeu.ae && itm.fCeu.mr && itm.fCeu.drc;
      if (!hasFCeu) {
        this.snackbarService.open(SnackbarTypes.ResourceUFNotAvailableForversion);
      }

      this.form.patchValue({
        fCeu: {
          en: hasFCeu ? itm.fCeu.en : '',
          co: hasFCeu ? itm.fCeu.co : '',
          gn: hasFCeu ? itm.fCeu.gn : '',
          ae: hasFCeu ? itm.fCeu.ae : '',
          mr: hasFCeu ? itm.fCeu.mr : '',
          drc: hasFCeu ? itm.fCeu.drc : ''
        },
        fCeuFileName: itm.fCeuFileName,
        fCeuFileDescription: itm.fCeuFileDescription
      });
    }
  }

  get formCrls() {
    return this.form.controls;
  }

  // Unité autre que la tonne : les valeurs par tonne se déduisent de l'UF et de la masse, elles ne se saisissent pas
  get ceuComputed(): boolean {
    return this.hasDensity && !this.formCrls.isComplexProduct.value && this.showDensity;
  }

  complexProductChange(event: any) {
    this.isComplexProduct = event.checked;
    this.revalidateIndicators();
  }

  // Lorsqu'on clique sur le checkbox Tuyau & Raccord
  pipeProductChange(event: any) {
    if (event.checked) {
      this.showDensity = false;
      this.formCrls.density.setValue(1);
    } else {
      this.showDensity = true;
    }

    this.isPipeProduct = event.checked;
    this.revalidateIndicators();
    if (this.isJoinProduct && event.checked) {
      this.isJoinProduct = false;
      this.formCrls.isJoinProduct.setValue(false);
    }
  }

  // Lorsqu'on clique sur le checkbox Tuyau & Raccord
  joinProductChange(event: any) {
    if (event.checked) {
      this.showDensity = false;
      this.formCrls.density.setValue(1);
    } else {
      this.showDensity = true;
    }

    this.isJoinProduct = event.checked;
    this.revalidateIndicators();
    if (this.isPipeProduct && event.checked) {
      this.isPipeProduct = false;
      this.formCrls.isPipeProduct.setValue(false);
    }
  }

  onDuplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 15, duplicate: (code: string) => this.productFormulaService.duplicate(resourceId, code) } })
      .afterClosed()
      .subscribe((res: any) => {
        if (res) {
          this.majStore(SnackbarTypes.duplicate);
          this.router.navigate([SiteMapUrl.MenuProduct, res.resourceId], {
            state: history.state
          });
        }
      });
  }

  async clickSave(product: Product) {
    this.revalidateIndicators();
    this.formCrls.density.markAsTouched();

    this.unSaved = false;
    this.formSubmitted = true;

    this.formCrls.ceu.get('gn')?.invalid;
    if (this.form.invalid) {
      // Les valeurs par tonne se déduisent de la masse : sans elle, c'est la masse qui manque, pas les indicateurs
      if (this.formCrls.density.errors?.positive) this.snackbarService.showMessage(this.translate.instant(this.formCrls.cableProduct?.value ? 'product_error_density_positive' : 'product_error_mass_positive'));
      else if (this.formCrls.density.invalid) this.snackbarService.open(SnackbarTypes.massRequired);
      else if (this.formCrls.ceu.invalid || this.formCrls.fCeu.invalid) this.snackbarService.open(SnackbarTypes.indicatorRequiert);
      if (this.formCrls.projectType.invalid) this.scrollToBottom();
      return;
    }

    product.ruade = this.currentItem.ruade;
    product.scopeId = this.currentItem.scopeId;
    product.owner = this.currentItem.owner;
    product.ownerId = this.currentItem.ownerId;
    product.productsComposition = this.currentItem.productsComposition;
    product.structureOperationTypesIds = this.currentItem.structureOperationTypesIds ?? [];

    if (this.isComplexProduct || !this.hasDensity) {
      delete product.density;
    }

    if (!this.hasDensity) {
      delete product.fCeu;
      delete product.fCeuFileDescription;
      delete product.fCeuFileName;
    } else {
      delete product.fileDescription;
      delete product.fileName;
    }

    if (this.isPipeProduct == true) product.pipeProduct = 1;
    else if (this.isJoinProduct == true) product.pipeProduct = 2;
    else product.pipeProduct = 0;

    // INSERT
    if (!this.formCrls?.resourceId?.value) {
      delete product.resourceId;

      if (product.familyId == GlobalConstants.EMPTY_GUID) {
        product.familyDisplayName = this.newFamilyName;
        product.familyCode = this.newFamilyCode;
      }

      let rep = this.productFormulaService.addProduct(product);
      rep.subscribe(
        (data: Product) => {
          this.currentItem = data;
          //this.uploadProof(data.resourceId);
          //this.uploadFceuProof(data.resourceId);
          this.majStore(SnackbarTypes.add);
          history.state.resourceId = data.resourceId;
          // Le POST enregistre déjà FileName et FileDescription ({id}.pdf)
          if (this.fileToUploadOnSave && data.resourceId) {
            const formData = new FormData();
            formData.append('file', this.fileToUploadOnSave, `${data.resourceId}.${this.fileToUpload?.name?.split('.').pop()}`);
            this.uploadService.uploadProof(data.resourceId, formData).subscribe(
              (event: any) => {
                if (event.type === HttpEventType.Response) {
                  if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
                  this.router.navigate([SiteMapUrl.MenuProduct, data.resourceId], {
                    state: history.state
                  });
                }
              },
              (error) => {
                this.snackbarService.open(SnackbarTypes.error);
              }
            );
          } else if (this.ceuFileToUploadOnSave && data.resourceId) {
            const formData = new FormData();
            formData.append('file', this.ceuFileToUploadOnSave, `${data.resourceId}.${this.fileToUpload?.name?.split('.').pop()}`);
            this.uploadService.uploadProof(data.resourceId, formData).subscribe(
              (event: any) => {
                if (event.type === HttpEventType.Response) {
                  if (this.fceuFileInput?.nativeElement) this.fceuFileInput.nativeElement.value = '';
                  this.router.navigate([SiteMapUrl.MenuProduct, data.resourceId], {
                    state: history.state
                  });
                }
              },
              (error) => {
                this.snackbarService.open(SnackbarTypes.error);
              }
            );
          } else {
            this.router.navigate([SiteMapUrl.MenuProduct, data.resourceId], {
              state: history.state
            });
          }
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }
    // UPDATE
    else {
      product.versionId = this.currentItem.ruade ? this.formCrls.versionId.value : undefined;
      if (product.familyId == GlobalConstants.EMPTY_GUID) {
        product.familyDisplayName = this.newFamilyName;
        product.familyCode = this.newFamilyCode;
      }
      let rep = this.productFormulaService.updateProduct(product);
      rep.subscribe(
        (data: Product) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.update);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }

    if (!this.isComplexProduct && this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    if (!this.isComplexProduct && this.fceuFileInput) {
      this.fceuFileInput.nativeElement.value = '';
    }
  }

  //Mise a jour de la liste des produits et formules
  majStore(snackbarMsgType: string) {
    this.store.select(StoreSelect.RESOURCES).subscribe((data) => {
      this.store.dispatch(new UpdateResourceAction(data));
    });

    this.productFormulaService.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  //Pop-up de suppression
  onDeleteDialog(resourceId: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_product') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (this.currentItem.canUnlink) {
          this.productFormulaService.unlink(resourceId).subscribe(
            () => {
              this.majStore(SnackbarTypes.delete);
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          );
        } else {
          this.deleteProduct(resourceId);
        }
      }
    });
  }

  onShare(resourceId: string) {
    var eusToShare: UserEntity[] = [];

    this.productFormulaService.getEUsToShareFormula(resourceId).subscribe((data) => {
      const dialogRef = this.dialog.open(DialogSelectEuShareComponent, {
        data: data
      });

      dialogRef.afterClosed().subscribe((dataResult) => {
        if (dataResult) {
          dataResult.forEach((el: any) => {
            eusToShare?.push({
              displayName: el?.displayName,
              euId: el?.resourceId
            });
          });
          this.productFormulaService.publishFormula(resourceId, eusToShare).subscribe({
            next: () => {
              this.snackbarService.open(SnackbarTypes.share);
            },
            error: () => {
              this.snackbarService.open(SnackbarTypes.error);
            }
          });
        }
      });
    });
  }

  //Supprimer un produit
  deleteProduct(resourceId: string) {
    if (resourceId) {
      this.productFormulaService.delete(resourceId).subscribe({
        next: () => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuProductFormula]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  addNewFamily() {
    const dialogRef = this.dialog.open(DialogInputComponent, {
      data: {
        title: this.translate.instant('dialog_add_family_title'),
        fields: [
          {
            key: 'name',
            label: this.translate.instant('dialog_family_name_label'),
            placeholder: this.translate.instant('dialog_family_name_label'),
            required: true
          },
          {
            key: 'code',
            label: this.translate.instant('dialog_family_code_label'),
            placeholder: this.translate.instant('common_code'),
            required: true
          }
        ]
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.newFamilyName = result.name;
        this.newFamilyCode = result.code || '';
        const tempFamily: Family = {
          familyId: GlobalConstants.EMPTY_GUID,
          id: GlobalConstants.EMPTY_GUID,
          code: this.newFamilyCode,
          displayName: this.newFamilyName,
          modality: '',
          hygrometry: false,
          Transports: [],
          MakeModels: [],
          ceModel: ''
        };

        if (!this.families.find((f) => f.familyId === GlobalConstants.EMPTY_GUID)) {
          this.families.unshift(tempFamily);
        }

        this.form.get('familyId')?.setValue(GlobalConstants.EMPTY_GUID);
        this.familyChange(GlobalConstants.EMPTY_GUID);
      }
    });
  }

  selectNewFamily() {
    const tempFamily = this.families.find((f) => f.familyId === GlobalConstants.EMPTY_GUID);
    if (tempFamily) {
      this.form.get('familyId')?.setValue(GlobalConstants.EMPTY_GUID);
      this.familyChange(GlobalConstants.EMPTY_GUID);
    }

    if (this.familySelect) {
      this.familySelect.close();
    }
  }

  editNewFamily(event: MouseEvent) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DialogInputComponent, {
      data: {
        title: this.translate.instant('dialog_edit_family_title'),
        fields: [
          {
            key: 'name',
            label: this.translate.instant('dialog_family_name_label'),
            placeholder: this.translate.instant('dialog_family_name_label'),
            value: this.newFamilyName,
            required: true
          },
          {
            key: 'code',
            label: this.translate.instant('dialog_family_code_label'),
            placeholder: this.translate.instant('common_code'),
            value: this.newFamilyCode,
            required: true
          }
        ]
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        const tempFamilyIndex = this.families.findIndex((f) => f.familyId === GlobalConstants.EMPTY_GUID);
        if (tempFamilyIndex !== -1) {
          this.families[tempFamilyIndex].displayName = result.name;
          this.families[tempFamilyIndex].code = result.code || '';

          if (this.form.get('familyId')?.value === GlobalConstants.EMPTY_GUID) {
            this.form.get('familyId')?.setValue(GlobalConstants.EMPTY_GUID);
          }
        }

        this.newFamilyName = result.name;
        this.newFamilyCode = result.code || '';
      }
    });
  }

  familyChange(id?: string) {
    if (id) {
      const currentFamily = this.families?.find((x) => x.familyId == id);
      if (currentFamily) {
        // Teneur en eau
        this.isHygrometryRateVisible = currentFamily.hygrometry ? true : false;
      }
    }
  }

  //Lors du changement de la densité
  changeWorkUnity(unitys: Unity[], id?: string) {
    if (id && unitys.length > 0) {
      let shortName = unitys.find((x) => x.workUnityId == id)?.shortDisplayName;
      this.hasDensity = shortName != WorkUnityCode.Ton;
      if (this.hasDensity) {
        this.form.get('fileDescription')?.setValue(null);
        this.form.get('fileName')?.setValue(null);
        this.fileToUploadOnSave = undefined;
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
      } else {
        this.form.get('fCeuFileDescription')?.setValue(null);
        this.form.get('fCeuFileName')?.setValue(null);
        this.ceuFileToUploadOnSave = undefined;
        if (this.fceuFileInput?.nativeElement) this.fceuFileInput.nativeElement.value = '';
      }
      this.revalidateIndicators();
    }
  }

  // Les validateurs lisent hasDensity : ils ne suivent un changement d'unité que s'ils sont réévalués
  revalidateIndicators() {
    [this.formCrls.ceu as FormGroup, this.formCrls.fCeu as FormGroup].forEach((group) => Object.values(group.controls).forEach((control) => control.updateValueAndValidity()));
    this.formCrls.density.updateValueAndValidity();
  }

  uploadProof(resourceId?: string, files?: any) {
    if (files && files.length > 0) {
      this.fileToUpload = <File>files[0];
    }

    if (this.fileToUpload) {
      const formData = new FormData();
      formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload?.name?.split('.').pop()}`);

      if (resourceId) {
        this.spinnerService.show();
        this.uploadService.uploadProof(resourceId, formData).subscribe(
          (event: any) => {
            if (event.type === HttpEventType.Response) {
              this.spinnerService.hide();
              this.form.get('fileDescription')?.setValue(event?.body);
              this.form.get('fileName')?.setValue(this.fileToUpload?.name);
              this.fileInput.nativeElement.value = '';
            }
          },
          () => {
            this.snackbarService.open(SnackbarTypes.error);
            this.spinnerService.hide();
          }
        );
      } else {
        this.form.get('fileName')?.setValue(this.fileToUpload?.name);
        this.fileToUploadOnSave = this.fileToUpload;
      }
    }
  }

  uploadFceuProof(resourceId?: string, files?: any) {
    if (files && files.length > 0) {
      this.fileToUpload = <File>files[0];
    }

    if (this.fileToUpload) {
      const formData = new FormData();
      formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload?.name?.split('.').pop()}`);

      if (resourceId) {
        this.spinnerService.show();
        this.uploadService.uploadProof(resourceId, formData).subscribe(
          (event: any) => {
            if (event.type === HttpEventType.Response) {
              this.spinnerService.hide();
              this.form.get('fCeuFileDescription')?.setValue(event?.body);
              this.form.get('fCeuFileName')?.setValue(this.fileToUpload?.name);
              this.fceuFileInput.nativeElement.value = '';
            }
          },
          () => {
            this.snackbarService.open(SnackbarTypes.error);
            this.spinnerService.hide();
          }
        );
      } else {
        this.form.get('fCeuFileName')?.setValue(this.fileToUpload?.name);
        this.ceuFileToUploadOnSave = this.fileToUpload;
      }
    }
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
    this.fileToUploadOnSave = undefined;
  }

  deleteFceuFile() {
    this.fceuFileInput.nativeElement.value = '';
    this.form.get('fCeuFileName')?.reset();
    this.form.get('fCeuFileDescription')?.reset();
    this.ceuFileToUploadOnSave = undefined;
  }

  setValue(event: any, inputFor: string) {
    this.formCrls.ceu.get(inputFor)?.setValue(event.value);
  }

  setFceuValue(event: any, inputFor: string) {
    this.formCrls.fCeu.get(inputFor)?.setValue(event.value);

    if (!event.value || isNaN(Number(event.value))) {
      this.formCrls.ceu.get(inputFor)?.setValue(event.value);
      return;
    }

    let density = this.formCrls.density.value;
    if (density && !isNaN(Number(density))) {
      this.formCrls.ceu.get(inputFor)?.setValue(Number(event.value) / Number(density));
    }
  }
  updateCeu(inputFor: string) {
    let fCeuValue = this.formCrls.fCeu.get(inputFor)?.value;
    if (fCeuValue && !isNaN(Number(fCeuValue))) {
      let density = this.formCrls.density.value;
      if (density && !isNaN(Number(density))) {
        this.formCrls.ceu.get(inputFor)?.setValue(Number(fCeuValue) / Number(density));
      }
    }
  }
  setDensity(event: any) {
    this.updateCeu('en');
    this.updateCeu('co');
    this.updateCeu('gn');
    this.updateCeu('ae');
    this.updateCeu('mr');
    this.updateCeu('drc');
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then(() => {
        this.favoriteId = '';
      });
    }
  }

  // Produit complexe

  // Ajouter un nouveau produit dans la composition
  addProductCompo(product: Product) {
    Object.assign(product, this.form.value);

    this.productFormulaService.getAll().subscribe((data) => {
      this.productFormulaService.getAll(GroupBy.favoriteDialog).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data,
            favoriteData: favoriteData
          }
        });

        dialogRef.afterClosed().subscribe((data) => {
          if (data) {
            if (!this.currentItem.productsComposition) {
              this.currentItem.productsComposition = [];
            }

            data.forEach((el: any) => {
              this.currentItem.productsComposition?.push({
                code: el?.code,
                produitId: el?.resourceId,
                displayName: el?.displayName,
                productWorkUnit: el?.workUnit,
                distance: 0
              });
            });
          }
        });
      });
    });
  }

  deleteProductCompo(index: number) {
    this.compositionTouched = true;

    if (this.currentItem?.productsComposition) this.currentItem.productsComposition.splice(index, 1);
  }

  // Modifier un produit dans la composition
  editProductCompo(item: FormulaComposition) {
    const dialogRef = this.dialog.open(DialogEditCompositionComponent, {
      data: {
        produits: {
          distance: item.distance,
          doubleCargo: item.doubleCargo,
          transportId: item.transportId,
          transportName: item.transportName,
          energyId: item?.energyId,
          energyName: item?.energyName,
          quantity: item?.quantity
        },
        transports: this.transports,
        energies: this.energies,
        displayForComplexProduct: true
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        item.transportId = data?.transportId;
        item.transportName = data?.transportName;
        item.energyId = data?.energyId;
        item.energyName = data?.energyName;
        item.doubleCargo = data?.doubleCargo;
        item.distance = data?.distance;
        item.quantity = data?.quantity;
        item.transportDeleted = false;
      }
    });
  }

  switchProduct(item: FormulaComposition) {
    this.productFormulaService.getAll(GroupBy.family).subscribe((data) => {
      this.productFormulaService.getAll(GroupBy.favorite).subscribe((favoriteData) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: this.filterCurrentComposition(data),
            favoriteData: this.filterCurrentComposition(favoriteData),
            isSingular: true,
            editMode: true
          }
        });

        dialogRef.afterClosed().subscribe(async (selectedProducts: any[]) => {
          const newProduct = selectedProducts?.[0];
          if (newProduct) {
            if (item.resourceCompositionId) {
              this.productFormulaService
                .updateFormulaComposition(item.resourceCompositionId, newProduct.resourceId)
                .toPromise()
                .then(() => this.snackbarService.open(SnackbarTypes.compositionUpdate))
                .catch(() => this.snackbarService.open(SnackbarTypes.error));
            }
            item.produitId = newProduct.resourceId;
            item.displayName = newProduct.displayName;
            item.code = newProduct.code;
            item.productWorkUnit = newProduct.workUnit;
          }
        });
      });
    });
  }

  filterCurrentComposition(products: any[]) {
    products.forEach((p) => {
      if (p.value) {
        p.value = this.filterCurrentComposition(p.value);
        if (p.value.length == 0) {
          products = products.filter((pr) => pr != p);
        }
      }
    });

    return products.filter((p) => p.resourceId != this.currentItem.resourceId);
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType) {
    return compare(pt1, pt2);
  }

  private scrollToBottom(): void {
    if (this.resourceSection?.nativeElement) {
      this.resourceSection.nativeElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }
}
